import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarIcon, Loader2, CheckCircle2, ArrowRight, ArrowLeft, FlaskConical, Waves, PersonStanding, FileText, AlertTriangle, PhoneCall, FileSearch, Zap } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "@/components/ui/sonner";
import { createBooking, getPaymentConfig } from "@/lib/api";
import { PACKAGES, inr } from "@/lib/packages";
import RiskChip from "@/components/RiskChip";
import PayButton from "@/components/PayButton";
import SeatsLeft from "@/components/SeatsLeft";

const STEPS = ["What's included", "Your details", "Payment"];
const PRIORITY_STEPS = ["Why priority", "Your details", "Confirm"];

const PRIORITY_POINTS = [
  { icon: PhoneCall, text: "Specialist call within 24–48 hours" },
  { icon: FileSearch, text: "Your report reviewed by our physician team" },
  { icon: Zap, text: "Fast-tracked imaging & next steps if needed" },
];

const EVAL_INCLUDES = [
  { icon: FlaskConical, title: "73+ Laboratory Biomarkers", desc: "Covering liver and metabolic health in one focused panel." },
  { icon: Waves, title: "Advanced Liver Imaging", desc: "Liver stiffness measurement and fat quantification." },
  { icon: PersonStanding, title: "120+ Health Data Points", desc: "Vitals, body composition, grip strength, CGM, HRV, sleep analysis and more when indicated." },
  { icon: FileText, title: "Precision Analytics & Report", desc: "Personalised risk insights and expert recommendations, prepared by our physicians." },
];

export const inputCls =
  "w-full rounded-xl border border-sa-beige bg-sa-bg px-4 py-2.5 text-sa-forest outline-none transition-colors focus:border-sa-copper";

export const StepDots = ({ step, labels = STEPS }) => (
  <div className="flex items-center gap-2" data-testid="booking-modal-steps">
    {labels.map((label, i) => {
      const n = i + 1;
      const active = step === n;
      const doneStep = step > n;
      return (
        <div key={label} className="flex items-center gap-2">
          {i > 0 && <span className={`h-px w-5 ${doneStep || active ? "bg-sa-copper" : "bg-sa-beige"}`} />}
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${
              active
                ? "bg-sa-forest text-white"
                : doneStep
                  ? "bg-sa-copper text-white"
                  : "border border-sa-beige text-sa-muted"
            }`}
            data-testid={`booking-step-dot-${n}`}
          >
            {doneStep ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
          </span>
          <span className={`hidden text-[11px] font-medium sm:inline ${active ? "text-sa-forest" : "text-sa-muted"}`}>
            {label}
          </span>
        </div>
      );
    })}
  </div>
);

// The complete booking flow on the QR-friendly /book page:
// card 1 = what the evaluation includes, card 2 = your details, card 3 = payment.
export const BookingFlow = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  // Smart pre-selection: ?tier= param (from assessment results) wins; then the
  // last assessment band stored in sessionStorage; default Basic.
  const [reco] = useState(() => {
    const q = searchParams.get("tier");
    if (q === "basic" || q === "advanced") return { tier: q, fromAssessment: true, priority: false };
    if (searchParams.get("priority") === "1") return { tier: "basic", fromAssessment: false, priority: true };
    try {
      const band = sessionStorage.getItem("sa_risk_band");
      if (band === "red") return { tier: "basic", band, fromAssessment: true, priority: true };
      if (band === "orange") return { tier: "advanced", band, fromAssessment: true, priority: false };
      if (band === "yellow") return { tier: "basic", band, fromAssessment: true, priority: false };
    } catch {}
    return { tier: "basic", fromAssessment: false, priority: false };
  });
  const [tier, setTier] = useState(reco.tier);
  const priority = reco.priority;
  // Autofill name/email/phone if the visitor just finished the assessment
  // (date + blood-collection fields always start blank).
  const [form, setForm] = useState(() => {
    let c = {};
    try { c = JSON.parse(sessionStorage.getItem("sa_contact") || "{}"); } catch {}
    return { name: c.name || "", email: c.email || "", phone: c.phone || "", address: "", city: "", pincode: "" };
  });
  const [date, setDate] = useState();
  const [calOpen, setCalOpen] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // 'paid' | 'callback'
  const [paymentOn, setPaymentOn] = useState(true);

  useEffect(() => {
    getPaymentConfig().then((c) => setPaymentOn(!!c?.enabled)).catch(() => setPaymentOn(false));
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validBasics = () => {
    if (!form.name.trim()) {
      toast.error("Please fill in your name.");
      return false;
    }
    if (!/^\d{10}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return false;
    }
    if (!priority && (!form.address.trim() || !form.city.trim() || !form.pincode.trim())) {
      toast.error("Please add your address, city and pincode for home blood collection.");
      return false;
    }
    if (!priority && !/^\d{6}$/.test(form.pincode.trim())) {
      toast.error("Pincode must be a 6-digit number.");
      return false;
    }
    return true;
  };

  const pkg = PACKAGES[tier] || PACKAGES.basic;
  // Biomarker count follows the selected package
  const includes = EVAL_INCLUDES.map((inc, i) =>
    i === 0 ? { ...inc, title: `${pkg.biomarkers} Laboratory Biomarkers` } : inc
  );

  const bookingPayload = () => ({
    ...form,
    program: priority ? "priority-consultation" : "precision-assessment",
    package: priority ? null : tier,
    preferred_date: date ? format(date, "yyyy-MM-dd") : null,
  });

  // Pay path: create the booking, then hand its id to Razorpay.
  const resolvePayLead = async () => {
    if (!validBasics()) return null;
    try {
      const booking = await createBooking(bookingPayload());
      setBookingId(booking.id);
      return { name: form.name, email: form.email, phone: form.phone, booking_id: booking.id };
    } catch {
      toast.error("Could not create your booking. Please try again.");
      return null;
    }
  };

  // Free path: request a callback without paying.
  const requestCallback = async () => {
    setLoading(true);
    try {
      if (!bookingId) {
        const booking = await createBooking(bookingPayload());
        setBookingId(booking.id);
      }
      setDone("callback");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Red-band path: priority consultation — no payment, specialist call-back.
  const requestPriority = async () => {
    setLoading(true);
    try {
      if (!bookingId) {
        const booking = await createBooking(bookingPayload());
        setBookingId(booking.id);
      }
      setDone("priority");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-flow">
      <h2 className="font-display text-xl font-medium text-sa-forest">
        {done
          ? (done === "paid" ? "Appointment requested." : done === "priority" ? "Priority request received." : "Request received.")
          : priority
            ? (step === 1 ? "Priority Consultation" : step === 2 ? "Your details" : "Confirm your request")
            : step === 1
              ? "Book your Precision Liver Evaluation"
              : step === 2
                ? "Your details"
                : "Reserve your slot"}
      </h2>
      <p className="mt-1 text-sm text-sa-muted">
        {done
          ? "You're all set."
          : priority
            ? (step === 1
                ? "Fast-track specialist review for high-risk results — step 1 of 3."
                : step === 2
                  ? "Takes under a minute — step 2 of 3."
                  : "No payment needed — step 3 of 3.")
            : step === 1
              ? "Everything your evaluation covers — step 1 of 3."
              : step === 2
                ? "Takes under a minute — step 2 of 3."
                : "Pay online, or let us call you — step 3 of 3."}
      </p>

      {!done && <div className="mt-3"><StepDots step={step} labels={priority ? PRIORITY_STEPS : STEPS} /></div>}

      <div className="mt-4">
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center py-6 text-center"
              data-testid="booking-modal-success"
            >
              <span className={`flex h-16 w-16 items-center justify-center rounded-full ${done === "priority" ? "bg-risk-red/10 text-risk-red" : "bg-sa-copper/15 text-sa-copper"}`}>
                {done === "priority" ? <PhoneCall className="h-8 w-8" /> : <CheckCircle2 className="h-8 w-8" />}
              </span>
              <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-sa-muted">
                {done === "paid"
                  ? `Thank you, ${form.name.split(" ")[0]}. Your evaluation is reserved and paid — our care team will call to confirm your home blood collection slot.`
                  : done === "priority"
                    ? `Thank you, ${form.name.split(" ")[0]}. Your priority request is in — our liver specialist team will call you within 24–48 hours.`
                    : `Thank you, ${form.name.split(" ")[0]}. Our care team will call you shortly to confirm your appointment.`}
              </p>
            </motion.div>
          ) : step === 1 ? (
            <motion.div key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} data-testid="booking-step-1">
              {reco.fromAssessment && !priority && (
                <div
                  className="mb-3 flex items-start gap-2 rounded-2xl border border-sa-copper/30 bg-sa-copper/5 px-4 py-2.5 text-[13px] leading-relaxed text-sa-forest"
                  data-testid="booking-reco-note"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sa-copper" />
                  <span>Based on your assessment, we recommend the <span className="font-semibold">{PACKAGES[reco.tier].name}</span> evaluation.</span>
                </div>
              )}
              {priority ? (
                <div data-testid="booking-priority-card">
                  <div className="rounded-2xl border border-risk-red/25 bg-risk-red/5 p-5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-risk-red/10 text-risk-red">
                        <AlertTriangle className="h-4.5 w-4.5" />
                      </span>
                      <p className="text-[14px] font-semibold text-sa-forest">Your results need priority attention</p>
                    </div>
                    <div className="mt-4 space-y-3">
                      {PRIORITY_POINTS.map((pt, i) => {
                        const Icon = pt.icon;
                        return (
                          <div key={pt.text} className="flex items-center gap-3 text-[13.5px] text-sa-forest" data-testid={`priority-point-${i}`}>
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-sa-copper shadow-sm">
                              <Icon className="h-4 w-4" />
                            </span>
                            {pt.text}
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-4 rounded-xl bg-white/70 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-sa-muted">
                      No payment needed. High-risk results go straight to our specialist team.
                    </p>
                  </div>
                </div>
              ) : (
                <>
              {/* Two parallel pathways — Basic vs Advance */}
              <div className="grid grid-cols-2 gap-2.5" data-testid="booking-packages">
                {[PACKAGES.basic, PACKAGES.advanced].map((p) => {
                  const on = tier === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setTier(p.key)}
                      data-testid={`booking-package-${p.key}`}
                      className={`relative rounded-2xl border p-3.5 text-left transition-all duration-300 ${
                        on
                          ? "border-sa-copper bg-white shadow-[0_10px_28px_-14px_rgba(198,123,92,0.5)] ring-1 ring-sa-copper"
                          : "border-sa-beige bg-sa-bg/60 hover:border-sa-copper/40"
                      }`}
                    >
                      {on && <CheckCircle2 className="absolute right-2.5 top-2.5 h-4 w-4 text-sa-copper" />}
                      <RiskChip risk={p.risk} hex={p.riskHex} text={p.riskText} className="w-full justify-center" />
                      <p className="mt-2 font-display text-[17px] font-semibold text-sa-forest">{p.name}</p>
                      <p className="text-[10.5px] uppercase tracking-wider text-sa-muted">Precision Liver Evaluation</p>
                      <div className="mt-2">
                        <span className="font-display text-xl font-medium text-sa-forest">{inr(p.price)}</span>
                      </div>
                      <p className="mt-1.5 text-[12px] font-medium text-sa-muted">{p.biomarkers} biomarkers</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 rounded-2xl border border-sa-beige bg-sa-bg/60 px-4 py-3">
                <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-sa-copper">
                  Founder's offer · first 1,000 evaluations
                </p>
                <SeatsLeft />
              </div>
              <div className="mt-4 space-y-2.5" data-testid="booking-includes">
                {includes.map((inc, i) => {
                  const Icon = inc.icon;
                  return (
                    <motion.div
                      key={inc.title}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 + i * 0.06, duration: 0.35 }}
                      className="flex items-start gap-3 rounded-2xl border border-sa-beige bg-white p-3.5"
                      data-testid={`booking-include-${i}`}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sa-bg text-sa-copper">
                        <Icon className="h-4.5 w-4.5" />
                      </span>
                      <div>
                        <p className="text-[14px] font-semibold leading-snug text-sa-forest">{inc.title}</p>
                        <p className="mt-0.5 text-[12.5px] leading-relaxed text-sa-muted">{inc.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
                </>
              )}
              <button
                onClick={() => setStep(2)}
                data-testid="booking-continue"
                className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.01] ${priority ? "bg-risk-red hover:bg-risk-red/90" : "bg-sa-forest hover:bg-sa-forest-soft"}`}
              >
                {priority ? "Request priority consultation" : "Continue to booking"} <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          ) : step === 2 ? (
            <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-3" data-testid="booking-step-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-sa-forest">Full name</label>
                <input value={form.name} onChange={update("name")} placeholder="Your name" data-testid="booking-modal-name" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="mb-1 block text-sm font-medium text-sa-forest">Email <span className="font-normal text-sa-muted">(optional)</span></label>
                  <input type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="booking-modal-email" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-sa-forest">Phone</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    data-testid="booking-modal-phone"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-sa-forest">Preferred date (optional)</label>
                <button
                  type="button"
                  onClick={() => setCalOpen((v) => !v)}
                  data-testid="booking-modal-date"
                  className="flex w-full items-center justify-between rounded-xl border border-sa-beige bg-sa-bg px-4 py-2.5 text-left text-sa-forest outline-none transition-colors focus:border-sa-copper"
                >
                  <span className={date ? "" : "text-sa-muted"}>{date ? format(date, "PPP") : "Pick a date"}</span>
                  <CalendarIcon className="h-4 w-4 text-sa-copper" />
                </button>
                <AnimatePresence initial={false}>
                  {calOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 flex justify-center rounded-xl border border-sa-beige bg-sa-bg p-2" data-testid="booking-date-calendar">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => { setDate(d); setCalOpen(false); }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {!priority && (
              <div className="rounded-2xl border border-sa-beige bg-sa-bg/60 p-3" data-testid="booking-collection-address">
                <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-sa-copper">
                  Home blood collection details
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-sa-forest">Address</label>
                    <textarea
                      value={form.address}
                      onChange={update("address")}
                      rows={2}
                      placeholder="Flat / house no., street, landmark"
                      data-testid="booking-modal-address"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-sa-forest">City</label>
                      <input value={form.city} onChange={update("city")} placeholder="e.g. Ahmedabad" data-testid="booking-modal-city" className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-sa-forest">Pincode</label>
                      <input
                        value={form.pincode}
                        onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                        placeholder="6-digit pincode"
                        inputMode="numeric"
                        maxLength={6}
                        data-testid="booking-modal-pincode"
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>
              </div>
              )}
              <button
                onClick={() => validBasics() && setStep(3)}
                data-testid="booking-modal-next"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-sa-forest px-7 py-3.5 font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.01] hover:bg-sa-forest-soft"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => setStep(1)} data-testid="booking-modal-back" className="flex items-center gap-1 text-[13px] text-sa-muted transition-colors hover:text-sa-forest">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </motion.div>
          ) : (
            <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-4" data-testid="booking-step-3">
              {priority ? (
                <>
                  <div className="rounded-2xl border border-risk-red/25 bg-risk-red/5 p-5 text-center">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-risk-red">Priority Consultation</p>
                    <p className="mt-1.5 font-display text-2xl font-medium text-sa-forest">No payment needed</p>
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-sa-muted">
                      Specialist call within 24–48 hours · report review · fast-tracked next steps
                    </p>
                  </div>
                  <button
                    onClick={requestPriority}
                    disabled={loading}
                    data-testid="booking-priority-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-risk-red px-7 py-3.5 font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.01] hover:bg-risk-red/90 disabled:opacity-70"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
                    Confirm priority request
                  </button>
                </>
              ) : (
                <>
              <div className="rounded-2xl border border-sa-beige bg-sa-bg/60 p-5 text-center">
                <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-sa-copper">{pkg.fullName}</p>
                <p className="mt-1 font-display text-4xl font-medium text-sa-forest" data-testid="booking-pay-price">{inr(pkg.price)}</p>
                <p className="mt-1.5 text-[12px] text-sa-muted">Founder's offer · first 1,000 evaluations</p>
              </div>
              {paymentOn ? (
                <PayButton
                  variant="copper"
                  showPromo={true}
                  tier={tier}
                  className="[&>div]:flex [&>div]:justify-center"
                  resolveLead={resolvePayLead}
                  onPaid={() => setDone("paid")}
                />
              ) : (
                <p className="text-center text-sm text-sa-muted">Online payment is coming soon.</p>
              )}
              <button
                onClick={requestCallback}
                disabled={loading}
                data-testid="booking-modal-callback"
                className="mx-auto flex items-center gap-1.5 text-sm font-medium text-sa-muted underline-offset-4 transition-colors hover:text-sa-forest hover:underline disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Request callback instead
              </button>
                </>
              )}
              <button onClick={() => setStep(2)} data-testid="booking-modal-back-2" className="flex items-center gap-1 text-[13px] text-sa-muted transition-colors hover:text-sa-forest">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingFlow;
